import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Calendar, Users, Server, AlertTriangle, CheckCircle2, ChevronRight, Mail, Send } from 'lucide-react';
import ERPAdvancedGrid from './erp/ERPAdvancedGrid';
import { erpToast } from './erp/ERPToast';
import { API, apiFetch } from '../lib/api';

export function RenewalManager() {
  const [licenses, setLicenses] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewingId, setRenewingId] = useState(null);
  const [renewForm, setRenewForm] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderSummary, setReminderSummary] = useState(null);

  // Grid state (all licenses)
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('daysLeft');
  const [sortDir, setSortDir]     = useState('asc');
  const [filters, setFilters]     = useState({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [licensesRes, remindersRes] = await Promise.all([
        apiFetch(`${API}/license/all`),
        apiFetch(`${API}/license/reminders`)
      ]);
      const licensesData = await licensesRes.json();
      const remindersData = await remindersRes.json();
      setLicenses(Array.isArray(licensesData) ? licensesData : []);
      setReminders(Array.isArray(remindersData) ? remindersData : []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
    setLoading(false);
  }, []);

  // \u2500\u2500 Reminder send (30d / 15d / 7d buckets) \u2500\u2500
  const sendReminders = async (dryRun = false) => {
    setSendingReminders(true);
    setReminderSummary(null);
    try {
      const res = await apiFetch(`${API}/license/send-reminders${dryRun ? '?dryRun=true' : ''}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || data.error) {
        erpToast.error(`Reminder run failed: ${data.error || res.status}`);
      } else {
        setReminderSummary(data);
        const msg = dryRun
          ? `Dry-run complete: would send ${data.buckets['30d'] + data.buckets['15d'] + data.buckets['7d']} reminders (${data.scanned} licenses scanned)`
          : `Reminders sent: ${data.sent} (${data.skipped} skipped, ${data.failed} failed) across ${data.scanned} licenses`;
        if (data.sent > 0 || dryRun) erpToast.success(msg);
        else erpToast.info(msg);
      }
    } catch (e) {
      erpToast.error(`Reminder run failed: ${e.message}`);
    }
    setSendingReminders(false);
  };

  const startRenewal = (license) => {
    setRenewingId(license.licenseId);
    setRenewForm({
      licenseId: license.licenseId,
      duration: '1 Year - Auto Renew',
      amount: license.amount,
      numberOfUsers: license.numberOfUsers,
      numberOfSystems: license.numberOfSystems,
      hasSupport: license.hasSupport,
      supportType: license.supportType || ''
    });
  };

  const cancelRenewal = () => {
    setRenewingId(null);
    setRenewForm(null);
  };

  const submitRenewal = async () => {
    if (!renewForm) return;
    try {
      const res = await apiFetch(`${API}/license/renew/${renewForm.licenseId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(renewForm)
      });
      const data = await res.json();
      if (data.success) {
        erpToast.success('License renewed successfully!');
        setRenewingId(null);
        setRenewForm(null);
        loadData();
      } else {
        erpToast.error('Renewal failed: ' + (data.error || 'unknown'));
      }
    } catch (err) {
      erpToast.error('Renewal failed: ' + err.message);
    }
  };

  const updateRenewForm = (key, value) => {
    setRenewForm(prev => ({ ...prev, [key]: value }));
  };

  // \u2500\u2500 Grid columns for All Licenses \u2500\u2500
  const columns = useMemo(() => ([
    { field: 'clientName',      header: 'Client',      visible: true, frozen: true },
    { field: 'clientId',        header: 'Client ID',   visible: true },
    { field: 'productId',       header: 'Product ID',  visible: true },
    { field: 'tier',            header: 'Tier',        visible: true },
    { field: 'numberOfUsers',   header: 'Users',       visible: true },
    { field: 'numberOfSystems', header: 'Systems',     visible: true },
    { field: 'amount',          header: 'Amount',      visible: true },
    { field: 'expiryDate',      header: 'Expiry',      visible: true },
    { field: 'daysLeft',        header: 'Days Left',   visible: true },
    { field: 'status',          header: 'Status',      visible: true },
    { field: 'email',           header: 'Email',       visible: false },
    { field: 'phone',           header: 'Phone',       visible: false },
    { field: 'licenseKey',      header: 'License Key', visible: false },
    { field: 'issuedDate',      header: 'Issued',      visible: false }
  ]), []);

  // Format rows for display (date strings + amount)
  const formattedLicenses = useMemo(() => licenses.map(l => ({
    ...l,
    expiryDate: l.expiryDate ? new Date(l.expiryDate).toLocaleDateString() : '',
    issuedDate: l.issuedDate ? new Date(l.issuedDate).toLocaleDateString() : '',
    amount: l.amount != null ? `\u20b9 ${Number(l.amount).toLocaleString()}` : ''
  })), [licenses]);

  const filteredSorted = useMemo(() => {
    let r = formattedLicenses;
    const af = Object.entries(filters).filter(([, v]) => v != null && String(v).trim() !== '');
    if (af.length) r = r.filter(row => af.every(([k, v]) => String(row[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    if (sortField) {
      r = [...r].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return r;
  }, [formattedLicenses, filters, sortField, sortDir]);

  const total = filteredSorted.length;
  const paginated = useMemo(() => filteredSorted.slice((page - 1) * pageSize, page * pageSize), [filteredSorted, page, pageSize]);

  const onSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };

  // Resolve the actual license object (with raw values) when ERPAdvancedGrid passes back the formatted row
  const findRaw = (row) => licenses.find(l => l.licenseId === row.licenseId) || row;

  const onEdit = (row) => startRenewal(findRaw(row));

  if (loading) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          <div style={{ width: 32, height: 32, border: '3px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p>Loading licenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }} data-testid="renewal-manager">
      {/* Action bar \u2014 Send reminders + Refresh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          className="erp-btn erp-btn-primary"
          onClick={() => sendReminders(false)}
          disabled={sendingReminders}
          data-testid="renewal-send-reminders"
          style={{ gap: 6 }}
        >
          <Send size={12} />
          {sendingReminders ? 'Sending\u2026' : 'Send Renewal Reminders (30/15/7d)'}
        </button>
        <button
          className="erp-btn"
          onClick={() => sendReminders(true)}
          disabled={sendingReminders}
          data-testid="renewal-dryrun-reminders"
          style={{ gap: 6 }}
        >
          <Mail size={12} /> Dry-run
        </button>
        <button className="erp-btn" onClick={loadData} data-testid="renewal-refresh" style={{ gap: 6 }}>
          <RefreshCw size={12} /> Refresh
        </button>
        {reminderSummary && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, fontSize: 11, color: '#475569' }}>
            <span><strong>30d:</strong> {reminderSummary.buckets['30d']}</span>
            <span><strong>15d:</strong> {reminderSummary.buckets['15d']}</span>
            <span style={{ color: '#dc2626' }}><strong>7d:</strong> {reminderSummary.buckets['7d']}</span>
            <span>Sent: {reminderSummary.sent} | Skipped: {reminderSummary.skipped} | Failed: {reminderSummary.failed}</span>
          </div>
        )}
      </div>

      {/* Expiring Soon Section \u2014 quick-action card stays for visibility */}
      {reminders.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title"><AlertTriangle size={14} style={{ display: 'inline', marginRight: 6, color: '#d97706' }} /> Expiring Soon (Next 30 Days)</span>
            <span className="card-badge" style={{ background: '#d97706', color: '#fff' }}>
              {reminders.length} LICENSE{reminders.length > 1 ? 'S' : ''}
            </span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1e293b', background: '#0f172a' }}>
                    <th style={tableHeaderStyle}>Client</th>
                    <th style={tableHeaderStyle}>Product ID</th>
                    <th style={tableHeaderStyle}>Tier</th>
                    <th style={tableHeaderStyle}>Expiry</th>
                    <th style={tableHeaderStyle}>Days Left</th>
                    <th style={tableHeaderStyle}>Bucket</th>
                    <th style={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reminders.map(lic => {
                    const bucket = lic.daysLeft <= 7 ? '7d' : lic.daysLeft <= 15 ? '15d' : '30d';
                    const bucketColor = bucket === '7d' ? '#dc2626' : bucket === '15d' ? '#f59e0b' : '#3b82f6';
                    return (
                      <tr key={lic.licenseId} style={{ borderBottom: '1px solid #1e293b' }}>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: 700, color: '#f8fafc', marginBottom: 2 }}>{lic.clientName}</div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>{lic.email}</div>
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#60a5fa' }}>{lic.productId}</span>
                        </td>
                        <td style={tableCellStyle}>{lic.tier}</td>
                        <td style={tableCellStyle}>{new Date(lic.expiryDate).toLocaleDateString()}</td>
                        <td style={tableCellStyle}>
                          <span style={{
                            color: lic.daysLeft <= 7 ? '#ef4444' : lic.daysLeft <= 15 ? '#f59e0b' : '#10b981',
                            fontWeight: 700
                          }}>
                            {lic.daysLeft} days
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                            background: `${bucketColor}20`, color: bucketColor
                          }}>{bucket}</span>
                        </td>
                        <td style={tableCellStyle}>
                          <button
                            className="erp-btn erp-btn-primary"
                            style={{ fontSize: 11, padding: '6px 12px' }}
                            onClick={() => startRenewal(lic)}
                            data-testid={`renewal-renew-${lic.licenseId}`}
                          >
                            Renew Now
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* All Licenses \u2014 ERPAdvancedGrid */}
      <ERPAdvancedGrid
        title={`All Licenses (${total})`}
        columns={columns}
        data={paginated}
        total={total}
        idField="licenseId"
        nameField="clientName"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        filters={filters}
        onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={false}
        showAddNew={false}
        onEdit={(row) => onEdit(row)}
        testId="renewal-licenses-grid"
      />

      {/* Inline renewal form \u2014 shown when a row is selected */}
      {renewingId && renewForm && (
        <div className="card" data-testid="renewal-form-panel">
          <div className="card-header">
            <span className="card-title">Renew License \u00b7 {renewingId}</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Duration</label>
                <select
                  className="form-select"
                  style={{ fontSize: 12 }}
                  value={renewForm.duration}
                  onChange={e => updateRenewForm('duration', e.target.value)}
                  data-testid="renewal-form-duration"
                >
                  <option>1 Year - Auto Renew</option>
                  <option>3 Years - Prepaid</option>
                  <option>Perpetual License</option>
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Number of Users</label>
                <input
                  className="form-input" type="number" min="1"
                  style={{ fontSize: 12 }}
                  value={renewForm.numberOfUsers}
                  onChange={e => updateRenewForm('numberOfUsers', parseInt(e.target.value) || 1)}
                  onFocus={(e) => e.target.select()}
                  data-testid="renewal-form-users"
                />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Number of Systems</label>
                <input
                  className="form-input" type="number" min="1"
                  style={{ fontSize: 12 }}
                  value={renewForm.numberOfSystems}
                  onChange={e => updateRenewForm('numberOfSystems', parseInt(e.target.value) || 1)}
                  onFocus={(e) => e.target.select()}
                  data-testid="renewal-form-systems"
                />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Amount (INR)</label>
                <input
                  className="form-input" type="number"
                  style={{ fontSize: 12 }}
                  value={renewForm.amount}
                  onChange={e => updateRenewForm('amount', e.target.value)}
                  onFocus={(e) => e.target.select()}
                  data-testid="renewal-form-amount"
                />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Support</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={renewForm.hasSupport}
                    onChange={e => updateRenewForm('hasSupport', e.target.checked)}
                    data-testid="renewal-form-support"
                  />
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Include Support</span>
                </div>
              </div>
              {renewForm.hasSupport && (
                <div>
                  <label className="form-label" style={{ fontSize: 11 }}>Support Type</label>
                  <select
                    className="form-select"
                    style={{ fontSize: 12 }}
                    value={renewForm.supportType}
                    onChange={e => updateRenewForm('supportType', e.target.value)}
                    data-testid="renewal-form-support-type"
                  >
                    <option value="">Select Type</option>
                    <option>Premium</option>
                    <option>Standard</option>
                    <option>Basic</option>
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="erp-btn" onClick={cancelRenewal} data-testid="renewal-form-cancel">
                Cancel
              </button>
              <button className="erp-btn erp-btn-primary" onClick={submitRenewal} data-testid="renewal-form-submit">
                Confirm Renewal <ChevronRight size={13} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const tableHeaderStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tableCellStyle = {
  padding: '12px 16px',
  fontSize: 12,
  color: '#cbd5e1'
};
